import { Pencil } from "@tamagui/lucide-icons";
import { useEffect, useState } from "react";
import { XStack, YStack } from "tamagui";

import {
  CODE_LENGTH,
  RESEND_TIMER_SECONDS,
  formatPhone,
} from "@/auth/usePhoneCodeAuth";
import { Button, LinkText, Text } from "@/components";
import { Input } from "@/components/forms/Input";
import {
  BORDERLESS_INPUT,
  OptionSeparator,
  PencilBadge,
} from "@/conversation/AskUserQuestionsPanel/OptionParts";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";

const CODE_TITLE = "Enter the verification code sent to your phone.";

const noop = () => {};

/**
 * Takes the code texted to the patient. Presentational: the caller owns the code, checks it, and
 * passes back `isVerifying` and any `errorText`. Submits on its own once the code is complete,
 * including when the phone autofills it from the text.
 */
export default function VerificationCodeCard({
  phone,
  code,
  onChangeCode,
  onSubmitCode,
  onResend,
  onChangeNumber,
  isVerifying = false,
  errorText,
  resendDelaySeconds = RESEND_TIMER_SECONDS,
}: {
  /** The 10-digit US number the code went to. */
  phone: string;
  code: string;
  onChangeCode: (code: string) => void;
  onSubmitCode: (code: string) => void;
  onResend: () => void;
  onChangeNumber: () => void;
  isVerifying?: boolean;
  errorText?: string;
  resendDelaySeconds?: number;
}) {
  const [secondsToResend, setSecondsToResend] = useState(resendDelaySeconds);

  useEffect(() => {
    if (secondsToResend <= 0) return;

    const timer = setTimeout(() => setSecondsToResend((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsToResend]);

  const canSubmit = code.length === CODE_LENGTH && !isVerifying;

  const changeCode = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    // Extra keystrokes past the sixth digit leave the code unchanged, so they don't resubmit it.
    const isNewCompleteCode = digits.length === CODE_LENGTH && digits !== code;
    onChangeCode(digits);
    if (isNewCompleteCode && !isVerifying) onSubmitCode(digits);
  };

  const submit = () => {
    if (!canSubmit) return;

    onSubmitCode(code);
  };

  const resend = () => {
    setSecondsToResend(resendDelaySeconds);
    onResend();
  };

  return (
    <>
      <PanelHeader
        question={CODE_TITLE}
        index={0}
        total={1}
        onPrev={noop}
        onNext={noop}
      />
      <XStack
        alignItems="center"
        gap="$100"
        paddingHorizontal="$150"
        marginTop="$-150"
        paddingBottom="$250"
      >
        <Text size="$sm">{formatPhone(phone)}</Text>
        <Button.Wrapper aria-label="Edit phone number" onPress={onChangeNumber}>
          <Pencil size="$150" color="$iconSecondary" />
        </Button.Wrapper>
      </XStack>
      <YStack paddingHorizontal="$150" paddingBottom="$150" gap="$50">
        <XStack alignItems="center" gap="$100">
          <PencilBadge />
          <Input
            flex={1}
            rootProps={{ flex: 1 }}
            frameProps={BORDERLESS_INPUT}
            placeholder="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            textContentType="oneTimeCode"
            autoFocus
            readOnly={isVerifying}
            value={code}
            onChangeText={changeCode}
            onSubmitEditing={submit}
          />
        </XStack>
        {errorText && (
          <Text size="$sm" color="$red" role="alert">
            {errorText}
          </Text>
        )}
      </YStack>
      <OptionSeparator />
      <XStack
        alignItems="center"
        gap="$200"
        paddingHorizontal="$150"
        paddingTop="$150"
        paddingBottom="$150"
      >
        <Text flex={1} size="$xs" color="$textSecondary">
          Didn't receive a code?{" "}
          {secondsToResend > 0 ? (
            `Resend (${secondsToResend})`
          ) : (
            <LinkText size="$xs" bold onPress={resend}>
              Resend
            </LinkText>
          )}
        </Text>
        <SkipOrSubmit
          showSkip={false}
          showSubmit
          submitDisabled={!canSubmit}
          isLast={false}
          onSkip={noop}
          onSubmit={submit}
        />
      </XStack>
    </>
  );
}
